import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, categoryArticles } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const articles = categoryArticles[activeSlug] || [];

  return (
    <Layout>
      <SEOHead
        title={`${activeCategory.name} - 福利吧`}
        description={`福利吧${activeCategory.name}专区，${activeCategory.description}每日更新最新${activeCategory.name}内容，帮助你以最低成本享受最高品质生活。`}
        keywords={`${activeCategory.name},福利吧,每日福利精选,优惠券,折扣`}
        canonicalUrl={`https://fuliba.com/category/${activeSlug}`}
      />
      <SchemaOrg type="WebPage" data={{ name: `${activeCategory.name} - 福利吧`, url: `https://fuliba.com/category/${activeSlug}` }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fuliba.com' },
          { '@type': 'ListItem', position: 2, name: activeCategory.name },
        ],
      }} />

      <div className="pt-16">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: activeCategory.name }]} />

        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <div className="flex flex-col items-center text-center mb-8">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                activeCategory.color === 'primary'
                  ? 'bg-primary-100 text-primary-600'
                  : activeCategory.color === 'accent'
                  ? 'bg-accent-100 text-accent-600'
                  : 'bg-secondary-100 text-secondary-600'
              }`}>
                <i className={`${activeCategory.icon} text-2xl`}></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {activeCategory.name}
              </h1>
              <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-lg">{activeCategory.description}</p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
              <div className="flex items-center bg-background-100 rounded-full p-1 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 no-underline cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article, index) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 no-underline cursor-pointer"
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <div className="relative overflow-hidden h-48">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      article.tagColor === 'primary'
                        ? 'bg-primary-500 text-background-50'
                        : article.tagColor === 'accent'
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-secondary-500 text-background-50'
                    }`}>
                      {article.tag}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h2 className="font-semibold text-foreground-950 text-base group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}